// Copyright ©️ 2026 eVolve MEP, LLC

import type { SpoolRequestId, UpdateSpoolRequestRequest } from '@evolvemep/api-ts';
import { LoadingOverlay, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';

export type SpoolRequestAction = {
  action: 'Rename' | 'Delete request';
  spoolId: SpoolRequestId;
  spool: RenderableSpool;
};

const UpdateSpoolForm = ({ spoolId, spoolName }: { spoolId: SpoolRequestId; spoolName: string }) => {
  const { updateRequest } = useSpoolRequestsViewerState();
  const form = useForm<UpdateSpoolRequestRequest>({ initialValues: { name: spoolName } });
  const onSubmit = async (change: UpdateSpoolRequestRequest) => {
    await updateRequest(spoolId, change);
  };
  useFormModal(form, onSubmit);
  return <TextInput label="Name" {...form.getInputProps('name')} />;
};

export const UpdateRequestModal = ({
  spoolAction,
  onClose,
}: {
  spoolAction: SpoolRequestAction | null;
  onClose: () => void;
}) => (
  <FormModal<UpdateSpoolRequestRequest>
    title={`Edit ${spoolAction?.spool.name}`}
    opened={spoolAction?.action === 'Rename'}
    onClose={onClose}
    type="submit"
  >
    {spoolAction?.action === 'Rename' ? (
      <UpdateSpoolForm spoolId={spoolAction.spoolId} spoolName={spoolAction.spool.name} />
    ) : (
      <LoadingOverlay visible />
    )}
  </FormModal>
);

export const DeleteRequestModal = ({
  spoolAction,
  onClose,
}: {
  spoolAction: SpoolRequestAction | null;
  onClose: () => void;
}) => {
  const { removeRequest } = useSpoolRequestsViewerState();
  const onDelete = async (spoolId: SpoolRequestId) => {
    await removeRequest(spoolId);
    notifications.show({
      title: 'Deletion successful',
      message: 'This spool request has been deleted successfully',
      color: 'green',
    });
  };

  return (
    <VariableTrackingModal
      variable={spoolAction?.action === 'Delete request' ? spoolAction : null}
      title="Confirm deletion"
      onClose={onClose}
      onConfirm={({ spoolId }) => onDelete(spoolId)}
      confirmationText="Delete"
      buttonColor="red"
    >
      {({ spool }) => (
        <Text>
          Are you sure you want to delete the request for <b>{spool.name}</b>?
        </Text>
      )}
    </VariableTrackingModal>
  );
};
